import React, { FC, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import { Button } from 'transfix-ui/components/Button'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { useQueryParams, BooleanParam } from 'use-query-params'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { DedicatedLanesExpiringBadge } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesExpiringBadge'
import { DedicatedLanesInquireModal } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesInquireModal'
import { DedicatedLanesModalItem } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesModalItem'
import { SubscriptionContainer } from 'apps/fleet-planner/components/dedicated-lanes/SubscriptionContainer'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { DedicatedLane, useDedicatedLanesContactUs } from 'apps/fleet-planner/types/apiTypes'
import { shouldShowExpirationTag } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import formatCurrency from 'utils/formatCurrency'
import { AllowedTimezones } from 'constants/AllowedTimezones'

const useDedicatedLaneDetailsModalStyles = makeStyles(theme => ({
  text: {
    color: brandColors.coolGray5,
  },
  divider: {
    border: `1px solid ${brandColors.coolGray2}`,
  },
  rateSection: {
    borderRadius: '4px',
    backgroundColor: brandColors.coolGray0,
    padding: theme.spacing(1.5),
  },
  subscribeText: {
    paddingBottom: 0,
    color: brandColors.coolGray5,
  },
}))

interface IDedicatedLanesContractModalProps {
  isOpen: boolean
  dedicatedLanes: DedicatedLane[]
  updateDedicatedLanes: (id: number, smsEnabled: boolean, newLaneId: string) => void
}

interface IDedicatedLanesContactUs {
  contactMessage?: string
  contactEmail?: string
  contactPhone?: string
}

export const DedicatedLanesContractModal: FC<IDedicatedLanesContractModalProps> = ({
  dedicatedLanes,
  isOpen,
  updateDedicatedLanes,
}) => {
  const { getFlag } = useFeatureFlagContext()
  const { userData } = useUserContext()
  const { user } = userData || {}
  const { email: userEmail } = user || {}
  const { enabled: isDedicatedLanesPageCapacityEnabled } = getFlag('dedicatedLanesPageCapacity')
  const { enabled: isDedicatedLanesContactModalEnabled } = getFlag('dedicatedLanesContactModal')
  const { enabled: isDedicatedLanesPageSmsEnabled } = getFlag('dedicatedLanesPageSms')
  const { enabled: isDedicatedLanesExpiringSoonTagEnabled } = getFlag(
    'dedicatedLanesExpiringSoonTag'
  )

  const { id: paramId } = useParams<{ id: string }>()
  const dedicatedLane =
    useMemo(() => dedicatedLanes.find(dl => dl.id === parseInt(paramId, 10)), [
      paramId,
      dedicatedLanes,
    ]) || {}
  const {
    capacityPerWeek = '0',
    carrier,
    deliveryCity,
    deliveryState,
    equipment = '',
    expirationDate = '',
    isOnlyBeer,
    isOnlyDropTrailer,
    pickupCity,
    pickupState,
    rate = '',
    shipper,
    smsNotificationsEnabled = false,
    startDate = '',
  } = dedicatedLane || {}
  const { name: shipperName } = shipper || {}
  const { email: carrierEmail = '', name: carrierName = '' } = carrier || {}
  const history = useHistory()
  const { trackEvent } = useFMSAnalytics()
  const { isMobile, isTablet } = useBreakpointContext()
  const isMobileOrTablet = isMobile || isTablet
  const classes = useDedicatedLaneDetailsModalStyles()
  const [{ mobileApp }] = useQueryParams({ mobileApp: BooleanParam })
  const mobileAppQueryParam = mobileApp ? '?mobileApp=1' : ''

  const closeModalHandler = () => {
    history.push(`/dedicated-lanes/contracts${mobileAppQueryParam}`)
  }
  const loadingMethodMessage = isOnlyDropTrailer ? 'Drop' : 'Live'

  const [openContactUsModal, setOpenContactUsModal] = useState(false)
  const { loading, mutate: createDedicatedLanesContactUs } = useDedicatedLanesContactUs({})
  const { openToast } = useGlobalToast()

  const contactUsSubmitHandler = async (data: IDedicatedLanesContactUs) => {
    try {
      await createDedicatedLanesContactUs({
        lane_id: dedicatedLane.id,
        email: data?.contactEmail,
        message: data?.contactMessage,
        phone: data?.contactPhone,
      })

      trackEvent('Dedicated Lanes', 'SUBMITTED_CONTACT_US_MODAL')
      setOpenContactUsModal(false)

      openToast({
        toastType: 'default',
        toastMessage: 'Message sent',
      })
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: e.message || 'Something went wrong, please contact us.',
      })
    }
  }

  const handleOpenCloseModal = () => {
    if (!openContactUsModal) {
      trackEvent('Dedicated Lanes', 'CLICKED_CONTACT_US_BUTTON')
    }

    setOpenContactUsModal(!openContactUsModal)
  }

  const showExpirationTag = shouldShowExpirationTag(dedicatedLane)

  return (
    <FullPageOverlay
      title={
        <Box
          display='flex'
          alignItems={isMobileOrTablet ? 'flex-start' : 'center'}
          flexDirection={isMobileOrTablet ? 'column' : 'row'}>
          <Box display='flex' alignItems='center'>
            <Box pr={1}>
              <Typography variant='h3'>{`${pickupCity}, ${pickupState}`}</Typography>
            </Box>
            <LaneArrowIcon size='large' />
            <Box pl={1}>
              <Typography variant='h3'>{`${deliveryCity}, ${deliveryState}`}</Typography>
            </Box>
          </Box>
          {dedicatedLane && showExpirationTag && isDedicatedLanesExpiringSoonTagEnabled && (
            <DedicatedLanesExpiringBadge
              marginTop={isMobileOrTablet ? '16px' : '0px'}
              marginLeft={isMobileOrTablet ? '0px' : '16px'}
            />
          )}
        </Box>
      }
      isOpen={isOpen}
      onClose={closeModalHandler}
      fullScreen={isMobile}
      showContentDividers
      maxWidth='md'
      footer={
        <>
          <Typography variant='body2'>Have a question or need to change something?</Typography>

          {isDedicatedLanesContactModalEnabled ? (
            <>
              <Button noPadding type='button' variant='text' onClick={handleOpenCloseModal}>
                Contact carrier team.
              </Button>
              <DedicatedLanesInquireModal
                isOpen={openContactUsModal}
                onClose={handleOpenCloseModal}
                cancelModalHandler={handleOpenCloseModal}
                submitHandler={contactUsSubmitHandler}
                isLoading={loading}
              />
            </>
          ) : (
            <div>Contact our carrier team.</div>
          )}

          {/* Needed to shift text position left. */}
          <div style={{ flex: '1 0 0' }} />
        </>
      }>
      <Box pl={1} pr={1}>
        {startDate && expirationDate ? (
          <>
            <div>
              <DedicatedLanesModalItem
                label='Start Date'
                text={
                  <FormattedDateTime
                    inSentence
                    datetime={startDate}
                    displayTimezone={moment.tz.guess() as AllowedTimezones}
                    format='dateAndYear'
                  />
                }
              />
              <DedicatedLanesModalItem
                label='End Date'
                text={
                  <FormattedDateTime
                    inSentence
                    datetime={expirationDate}
                    displayTimezone={moment.tz.guess() as AllowedTimezones}
                    format='dateAndYear'
                  />
                }
              />
            </div>
            {/* Divider */}
            <Box pt={1} pb={2}>
              <div className={classes.divider} />
            </Box>
          </>
        ) : (
          expirationDate && (
            <>
              <DedicatedLanesModalItem
                label='Rate Expiration'
                text={
                  <FormattedDateTime
                    inSentence
                    datetime={expirationDate}
                    displayTimezone={moment.tz.guess() as AllowedTimezones}
                    format='dateAndYear'
                  />
                }
              />
              {/* Divider */}
              <Box pt={1} pb={2}>
                <div className={classes.divider} />
              </Box>
            </>
          )
        )}

        <DedicatedLanesModalItem label='Load Type' text={loadingMethodMessage} />
        <DedicatedLanesModalItem label='Equipment Type' text={equipment} />
        {isDedicatedLanesPageCapacityEnabled && (
          <DedicatedLanesModalItem
            label='Volume'
            text={`Up to ${capacityPerWeek} loads per week`}
            textSecondary={`You will receive 0-${capacityPerWeek} load offers per week`}
          />
        )}
        {/* Divider */}
        <Box pt={1} pb={2}>
          <div className={classes.divider} />
        </Box>
        {shipperName && <DedicatedLanesModalItem label='Shipper' text={shipperName} />}
        {isOnlyBeer && <DedicatedLanesModalItem label='Commodity Type' text='Beer' />}
        <div className='fs-mask'>
          <DedicatedLanesModalItem
            label='Contact Email'
            text={carrierEmail}
            additionalInfo={
              <div>
                {userEmail === carrierEmail && isDedicatedLanesPageSmsEnabled && (
                  <SubscriptionContainer
                    updateDedicatedLanes={updateDedicatedLanes}
                    dedicatedLaneId={parseInt(paramId, 10)}
                    smsNotificationsEnabled={smsNotificationsEnabled}
                  />
                )}
              </div>
            }
          />
        </div>

        <Box pt={1} pb={2}>
          <div className={classes.divider} />
        </Box>
        <DedicatedLanesModalItem label='Carrier' text={carrierName} />
        <Box pt={1} pb={3}>
          <div className={classes.divider} />
        </Box>
        {/* Rate */}
        <Box display='flex' alignItems='center' className={classes.rateSection}>
          <div>
            <Typography>Rate (per load)</Typography>
            <Typography className={classes.text} variant='caption'>
              Rate does not include fuel surcharges which may apply.
            </Typography>
          </div>
          <Box marginLeft='auto'>
            <Typography variant='body1'>{formatCurrency(rate) || 'Not Available'}</Typography>
          </Box>
        </Box>
      </Box>
    </FullPageOverlay>
  )
}
